import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import { money } from "./accountEditorUi";

/**
 * Edit money on a custom (Others) entry — same “Edit account figures” pattern as linked modules,
 * with agreed/cost/discount editable here and agency commission/bonus claimed in the same place.
 */
const EditOtherEntryFiguresModal = ({
  open,
  onClose,
  row = null,
  showAgencyEarnings = false,
  onSaved,
}) => {
  const [values, setValues] = useState({
    agreedAmount: "",
    cost: "",
    discount: "",
    commission: "",
    bonus: "",
    isCommissionClaimed: false,
    isBonusClaimed: false,
  });

  useEffect(() => {
    if (!open) return;
    setValues({
      agreedAmount: row?.agreedAmount ?? "",
      cost: row?.cost ?? "",
      discount: row?.discount ?? "",
      commission: row?.commission ?? "",
      bonus: row?.bonus ?? "",
      isCommissionClaimed: Boolean(row?.isCommissionClaimed),
      isBonusClaimed: Boolean(row?.isBonusClaimed),
    });
    // Only hydrate when opening so typing is not reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.uuid, row?.id]);

  const handleChange = (field) => (e) =>
    setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const handleToggle = (field) => (e) =>
    setValues((prev) => ({ ...prev, [field]: e.target.checked }));

  const handleSave = () => {
    onSaved?.(values);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold m-0">Edit account figures</h3>
          <IconButton onClick={onClose} aria-label="Close">
            <IoClose className="text-primary" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-app-border bg-app-surface-muted/40 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-app-text-muted m-0">
                Paid
              </p>
              <p className="text-sm font-semibold text-app-text m-0 mt-0.5">
                {money(row?.paidAmount)}
              </p>
            </div>
            <div className="rounded-lg border border-app-border bg-app-surface-muted/40 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-app-text-muted m-0">
                Due
              </p>
              <p className="text-sm font-semibold text-app-text m-0 mt-0.5">
                {money(row?.dueAmount)}
              </p>
            </div>
          </div>

          <TextField
            size="small"
            fullWidth
            label="Agreed"
            type="number"
            value={values.agreedAmount}
            onChange={handleChange("agreedAmount")}
          />
          <TextField
            size="small"
            fullWidth
            label="Agent cost"
            type="number"
            value={values.cost}
            onChange={handleChange("cost")}
          />
          <TextField
            size="small"
            fullWidth
            label="Discount"
            type="number"
            value={values.discount}
            onChange={handleChange("discount")}
          />

          {showAgencyEarnings && (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-app-text-muted m-0">
                Agency earnings
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-lg border border-app-border/70 px-3 py-2 space-y-1.5">
                  <TextField
                    size="small"
                    fullWidth
                    label="Commission"
                    type="number"
                    value={values.commission}
                    onChange={handleChange("commission")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={values.isCommissionClaimed}
                        onChange={handleToggle("isCommissionClaimed")}
                      />
                    }
                    label="Received — count in profit"
                  />
                </div>
                <div className="rounded-lg border border-app-border/70 px-3 py-2 space-y-1.5">
                  <TextField
                    size="small"
                    fullWidth
                    label="Bonus"
                    type="number"
                    value={values.bonus}
                    onChange={handleChange("bonus")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={values.isBonusClaimed}
                        onChange={handleToggle("isBonusClaimed")}
                      />
                    }
                    label="Received — count in profit"
                  />
                </div>
              </div>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOtherEntryFiguresModal;
